import { baseUrl } from './api.js';

/**
 * Krok KR1 — penezenka kancelare pro AI. Stejny vzor jako
 * `assistant-settings-api.ts`: raw `fetch`, routy jeste nejsou v generovanem
 * `@reos/api-client` schematu.
 */

export interface LedgerEntryDto {
  readonly id: string;
  readonly kind: string;
  readonly amountCzk: string;
  readonly referenceType: string | null;
  readonly referenceId: string | null;
  readonly reason: string;
  readonly createdBy: string;
  readonly createdAt: string;
}

export interface DailySpendDto {
  readonly day: string;
  readonly spentCzk: string;
}

export interface UsageBreakdownDto {
  readonly referenceType: string;
  readonly calls: number;
  readonly spentCzk: string;
}

export interface BillingDto {
  readonly name: string;
  readonly company?: string;
  readonly ico?: string;
  readonly vatId?: string;
  readonly street?: string;
  readonly city?: string;
  readonly zip?: string;
  readonly country?: string;
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${baseUrl}${path}`, { credentials: 'include', ...init });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { detail?: string; title?: string } | null;
    throw new Error(body?.detail ?? body?.title ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

export function getCreditBalance(): Promise<{ balanceCzk: string }> {
  return req('/v1/ai/credits/balance');
}

export function getCreditLedger(): Promise<{ items: LedgerEntryDto[] }> {
  return req('/v1/ai/credits/ledger');
}

export function getCreditUsage(): Promise<{ daily: DailySpendDto[]; breakdown: UsageBreakdownDto[] }> {
  return req('/v1/ai/credits/usage');
}

export function getTopupConfig(): Promise<{ quickAmountsCzk: string[]; minAmountCzk: string; vatPayer: boolean }> {
  return req('/v1/ai/credits/topup-config');
}

export type TopupResult =
  | { readonly intentId: string; readonly method: 'card'; readonly redirectUrl: string }
  | {
      readonly intentId: string;
      readonly method: 'bank_transfer';
      readonly variableSymbol: string;
      readonly amountCzkGross: string;
      readonly proformaDocumentRef: string;
      readonly dueAt: string;
    };

export function createTopup(input: {
  method: 'card' | 'bank_transfer';
  amountCzkNet: string;
  email: string;
  billing: BillingDto;
}): Promise<TopupResult> {
  return req('/v1/ai/credits/topup', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export type AiKeyProvider = 'anthropic' | 'openai';

export interface ProviderKeyStatusDto {
  readonly provider: AiKeyProvider;
  readonly status: 'ok' | 'invalid';
  readonly secretHint: string;
  readonly lastCheckedAt: string | null;
}

export function getProviderKey(): Promise<{ key: ProviderKeyStatusDto | null }> {
  return req('/v1/ai/provider-key');
}

export function saveProviderKey(provider: AiKeyProvider, apiKey: string): Promise<{ key: ProviderKeyStatusDto }> {
  return req('/v1/ai/provider-key', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ provider, apiKey }),
  });
}

export function removeProviderKey(): Promise<{ ok: boolean }> {
  return req('/v1/ai/provider-key', { method: 'DELETE' });
}
